import { getAPIURL } from "./config";
import {
  encodeProjectLevelName,
  parseProjectLevelName,
  PROJECT_LEVEL_PLACEMENT_END,
  PROJECT_LEVEL_SECTION_ID,
  PROJECT_LEVEL_SECTION_NAME,
  normalizeProjectLevelPlacement,
} from "../components/ProjectsV2/ProjectsSchedule/projectsScheduleHelpers";

const APPLIED_STORAGE_KEY = "aqmol_project_schedule_applied_templates";

const createId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const parseApiError = (errorText, fallback) => {
  let message = fallback;
  try {
    const err = JSON.parse(errorText);
    if (typeof err.detail === "string") {
      message = err.detail;
    } else if (Array.isArray(err.detail)) {
      message = err.detail
        .map((item) => item.msg || JSON.stringify(item))
        .join("; ");
    } else if (err.message) {
      message = err.message;
    }
  } catch {
    if (errorText) message += `. ${errorText}`;
  }
  return message;
};

const requestJson = async (path, { method = "GET", body } = {}) => {
  const response = await fetch(`${getAPIURL()}${path}`, {
    method,
    headers: {
      Accept: "application/json",
      ...(body != null ? { "Content-Type": "application/json" } : {}),
    },
    credentials: "include",
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseApiError(
        errorText,
        `Ошибка шаблона графика: ${response.status} ${response.statusText}`,
      ),
    );
  }

  if (response.status === 204) return { message: "ok" };
  try {
    return await response.json();
  } catch {
    return { message: "ok" };
  }
};

const sortByOrder = (items = []) =>
  [...items].sort(
    (a, b) =>
      (a.order_index ?? 0) - (b.order_index ?? 0) ||
      String(a.id).localeCompare(String(b.id)),
  );

const readAppliedStore = () => {
  try {
    const raw = localStorage.getItem(APPLIED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const getAppliedScheduleTemplate = (projectId) => {
  if (projectId == null || projectId === "") return null;
  return readAppliedStore()[String(projectId)] || null;
};

export const setAppliedScheduleTemplate = (projectId, template) => {
  if (projectId == null || projectId === "" || !template) return;
  const all = readAppliedStore();
  all[String(projectId)] = {
    id: template.id,
    name: template.name,
  };
  localStorage.setItem(APPLIED_STORAGE_KEY, JSON.stringify(all));
};

export const createEmptyTemplateSection = (catalogSection) => ({
  id: createId(),
  catalog_section_id: catalogSection?.id ?? null,
  name: catalogSection?.name || "Раздел",
  order_index: 0,
  items: [],
});

export const createEmptyTemplateItem = ({
  name,
  isMilestone = false,
  unitWeightPercent = 0,
  orderIndex = 0,
}) => ({
  id: createId(),
  name: String(name || "").trim(),
  is_milestone: Boolean(isMilestone),
  unit_weight_percent: isMilestone ? 0 : Number(unitWeightPercent) || 0,
  order_index: orderIndex,
  predecessor_ids: [],
  successor_ids: [],
});

export const createProjectLevelTemplateSection = ({
  name,
  placement = "start",
} = {}) => ({
  id: createId(),
  catalog_section_id: null,
  name: String(name || "").trim() || PROJECT_LEVEL_SECTION_NAME,
  placement: normalizeProjectLevelPlacement(placement),
  order_index: 0,
  items: [],
  is_project_level: true,
});

export const isProjectLevelTemplateSection = (section) =>
  Boolean(section?.is_project_level) ||
  section?.id === PROJECT_LEVEL_SECTION_ID ||
  (section?.catalog_section_id == null &&
    section?.id != null &&
    String(section.id).startsWith("plg_"));

export const ensureProjectLevelTemplateSection = (sections = []) =>
  (Array.isArray(sections) ? sections : []).map((section) =>
    isProjectLevelTemplateSection(section)
      ? {
          ...section,
          catalog_section_id: null,
          is_project_level: true,
          placement: normalizeProjectLevelPlacement(section.placement),
          name:
            String(section.name || "").trim() || PROJECT_LEVEL_SECTION_NAME,
        }
      : section,
  );

export const arrangeTemplateSections = (sections = []) => {
  const start = [];
  const catalog = [];
  const end = [];
  ensureProjectLevelTemplateSection(sections).forEach((section) => {
    if (!isProjectLevelTemplateSection(section)) catalog.push(section);
    else if (section.placement === PROJECT_LEVEL_PLACEMENT_END) end.push(section);
    else start.push(section);
  });
  return [...start, ...catalog, ...end].map((section, index) => ({
    ...section,
    order_index: index,
  }));
};

const toTemplateItem = (item, visibleName) => ({
  id: item.id,
  name: visibleName,
  is_milestone: Boolean(item.is_milestone),
  unit_weight_percent: Number(item.unit_weight_percent) || 0,
  order_index: item.order_index ?? 0,
  predecessor_ids: Array.isArray(item.predecessor_ids)
    ? item.predecessor_ids
    : [],
  successor_ids: Array.isArray(item.successor_ids) ? item.successor_ids : [],
});

export const templateFromApi = (data = {}) => {
  const items = Array.isArray(data.items) ? data.items : [];
  const sections = [];
  const catalogMap = new Map();
  const groupMap = new Map();

  items.forEach((item) => {
    const isProjectLevel =
      item.project_section_id == null || item.project_section_id === "";
    if (!isProjectLevel) {
      const catalogId = item.project_section_id;
      const key = String(catalogId);
      if (!catalogMap.has(key)) {
        const section = {
          id: `section_${catalogId}`,
          catalog_section_id: catalogId,
          name: item.project_section_name || `Раздел ${catalogId}`,
          order_index: sections.length,
          is_project_level: false,
          items: [],
        };
        catalogMap.set(key, section);
        sections.push(section);
      }
      catalogMap.get(key).items.push(toTemplateItem(item, item.name));
      return;
    }

    const meta = parseProjectLevelName(item.name);
    const groupId = meta.encoded ? meta.groupId : PROJECT_LEVEL_SECTION_ID;
    if (!groupMap.has(groupId)) {
      const section = {
        id: groupId,
        catalog_section_id: null,
        name: meta.groupName,
        placement: meta.placement,
        order_index: sections.length,
        is_project_level: true,
        items: [],
      };
      groupMap.set(groupId, section);
      sections.push(section);
    }
    if (meta.isSentinel) return;
    groupMap
      .get(groupId)
      .items.push(toTemplateItem(item, meta.visibleName || item.name));
  });

  const normalized = arrangeTemplateSections(
    sections.map((section) => ({
      ...section,
      items: sortByOrder(section.items),
    })),
  );

  return {
    id: data.id,
    name: data.name || "",
    description: data.description || "",
    building_type: data.building_type || null,
    sections: normalized,
    items_count: data.items_count ?? items.length,
    sections_count:
      data.sections_count ??
      normalized.filter((section) => !isProjectLevelTemplateSection(section))
        .length,
    updated_at: data.updated_at || data.updatedAt || null,
  };
};

const addLink = (links, seen, itemKey, predecessorKey) => {
  if (!itemKey || !predecessorKey || itemKey === predecessorKey) return;
  const signature = `${itemKey}>${predecessorKey}`;
  if (seen.has(signature)) return;
  seen.add(signature);
  links.push({
    item_temp_key: itemKey,
    predecessor_temp_key: predecessorKey,
  });
};

export const templateToApiPayload = ({
  name,
  description,
  building_type,
  sections = [],
}) => {
  const items = [];
  const pendingLinks = [];

  arrangeTemplateSections(sections).forEach((section) => {
    const isProjectLevel = isProjectLevelTemplateSection(section);
    const catalogId = isProjectLevel
      ? null
      : Number(section.catalog_section_id);
    if (
      !isProjectLevel &&
      (!Number.isFinite(catalogId) || catalogId <= 0)
    ) {
      throw new Error(
        `У раздела «${section.name || "без названия"}» нет id справочника`,
      );
    }

    const sectionItems = sortByOrder(section.items);
    if (isProjectLevel) {
      items.push({
        temp_key: `plg_s_${section.id}`,
        project_section_id: null,
        name: encodeProjectLevelName({
          placement: section.placement,
          groupId: section.id,
          groupName: section.name,
          visibleName: "",
        }),
        is_milestone: true,
        unit_weight_percent: 0,
        order_index: 0,
      });
    }

    sectionItems.forEach((item, index) => {
      const itemName = String(item.name || "").trim();
      if (!itemName) return;
      const tempKey = String(item.id);
      items.push({
        temp_key: tempKey,
        project_section_id: isProjectLevel ? null : catalogId,
        name: isProjectLevel
          ? encodeProjectLevelName({
              placement: section.placement,
              groupId: section.id,
              groupName: section.name,
              visibleName: itemName,
            })
          : itemName,
        is_milestone: Boolean(item.is_milestone),
        unit_weight_percent:
          item.is_milestone || isProjectLevel
            ? 0
            : Number(item.unit_weight_percent) || 0,
        order_index: isProjectLevel ? index + 1 : item.order_index ?? index,
      });
      (item.predecessor_ids || []).forEach((predId) => {
        pendingLinks.push({ itemKey: tempKey, predecessorKey: String(predId) });
      });
      (item.successor_ids || []).forEach((succId) => {
        pendingLinks.push({
          itemKey: String(succId),
          predecessorKey: tempKey,
        });
      });
    });
  });

  const knownKeys = new Set(items.map((item) => item.temp_key));
  const links = [];
  const seen = new Set();
  pendingLinks.forEach(({ itemKey, predecessorKey }) => {
    if (!knownKeys.has(itemKey) || !knownKeys.has(predecessorKey)) return;
    addLink(links, seen, itemKey, predecessorKey);
  });

  return {
    name: String(name || "").trim(),
    building_type: building_type || null,
    description: String(description || "").trim(),
    items,
    links,
  };
};

export const getScheduleTemplateStats = (template) => {
  const nested = Array.isArray(template?.sections) ? template.sections : [];
  if (nested.length > 0) {
    const items = nested.flatMap((section) => section.items || []);
    return {
      sectionsCount: nested.filter(
        (section) => !isProjectLevelTemplateSection(section),
      ).length,
      workTypesCount: items.filter((item) => !item.is_milestone).length,
      milestonesCount: items.filter((item) => item.is_milestone).length,
      itemsCount: items.length,
    };
  }

  const itemsCount = Number(template?.items_count) || 0;
  return {
    sectionsCount: Number(template?.sections_count) || 0,
    workTypesCount: itemsCount,
    milestonesCount: null,
    itemsCount,
  };
};

const normalizeTemplatesList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.templates)) return data.templates;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items) && data.id == null) return data.items;
  return [];
};

const resolveProjectSectionName = (id, projectSections = []) => {
  const found = projectSections.find((section) =>
    [section.id, section.project_section_id, section.section_id].some(
      (value) => value != null && String(value) === String(id),
    ),
  );
  return found?.name || `Раздел #${id}`;
};

const normalizeSkippedSections = (skipped, projectSections = []) => {
  if (!Array.isArray(skipped)) return [];
  return skipped.map((item) => {
    if (item && typeof item === "object") {
      const id = item.project_section_id ?? item.id;
      return {
        sectionName:
          item.name ||
          item.project_section_name ||
          resolveProjectSectionName(id, projectSections),
        reason: item.reason || "Пропущен",
      };
    }
    return {
      sectionName: resolveProjectSectionName(item, projectSections),
      reason: "Пропущен",
    };
  });
};

/**
 * GET /project_progress/templates
 */
export const getScheduleTemplates = async (params = {}) => {
  const search = new URLSearchParams();
  if (params.building_type) {
    search.set("building_type", params.building_type);
  }
  const query = search.toString();
  const data = await requestJson(
    `project_progress/templates${query ? `?${query}` : ""}`,
  );
  return normalizeTemplatesList(data).map((tpl) => ({
    ...tpl,
    description: tpl.description || "",
    building_type: tpl.building_type || null,
    items_count: tpl.items_count ?? 0,
    sections_count: tpl.sections_count ?? 0,
  }));
};

/**
 * GET /project_progress/templates/{template_id}
 */
export const getScheduleTemplate = async (templateId) => {
  const data = await requestJson(`project_progress/templates/${templateId}`);
  return templateFromApi(data);
};

/**
 * POST /project_progress/templates
 */
export const createScheduleTemplate = async (payload) => {
  const body = templateToApiPayload(payload);
  if (!body.name) {
    throw new Error("Укажите название шаблона");
  }
  const data = await requestJson("project_progress/templates", {
    method: "POST",
    body,
  });
  return templateFromApi(data);
};

/**
 * PATCH /project_progress/templates/{template_id}
 */
export const updateScheduleTemplate = async (templateId, payload) => {
  const body = templateToApiPayload(payload);
  if (!body.name) {
    throw new Error("Укажите название шаблона");
  }
  const data = await requestJson(`project_progress/templates/${templateId}`, {
    method: "PATCH",
    body,
  });
  return templateFromApi(data);
};

/**
 * DELETE /project_progress/templates/{template_id}
 */
export const deleteScheduleTemplate = async (templateId) =>
  requestJson(`project_progress/templates/${templateId}`, {
    method: "DELETE",
  });

/**
 * POST /project_progress/projects/{project_id}/apply_template
 */
export const applyScheduleTemplateToProject = async (
  projectId,
  template,
  projectSections = [],
) => {
  const data = await requestJson(
    `project_progress/projects/${projectId}/apply_template`,
    {
      method: "POST",
      body: {
        template_id: Number(template.id),
        replace_existing: false,
      },
    },
  );

  const createdWorkTypes = Number(data?.created_work_types) || 0;
  const createdLinks = Number(data?.created_links) || 0;
  const skipped = normalizeSkippedSections(
    data?.skipped_sections,
    projectSections,
  );

  if (createdWorkTypes > 0 || createdLinks > 0) {
    setAppliedScheduleTemplate(projectId, template);
  }

  return {
    applied:
      createdWorkTypes > 0
        ? [
            {
              sectionName: template.name || "Шаблон",
              added: [`${createdWorkTypes} видов работ/вех`],
            },
          ]
        : [],
    skipped,
    unmatched: [],
    errors: [],
    relationsUpdated: createdLinks,
    appliedToProject: createdWorkTypes > 0 || createdLinks > 0,
    message: data?.message || "",
    created_work_types: createdWorkTypes,
    created_links: createdLinks,
  };
};
